import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Plus, Flame, Clock, TrendingUp, Users, Search, Trophy, HelpCircle } from "lucide-react";
import {
  listForumCategories,
  listForumPosts,
  forumLeaderboard,
  listUnansweredQuestions,
} from "@/lib/forum.functions";
import { TagCloud } from "@/components/forum/TagCloud";
import { PostCard, type ForumPostRow } from "@/components/forum/PostCard";
import { useAuth } from "@/hooks/use-auth";


const TITLE = "Fritidsforum – community för husbil & husvagn | AK Husbil";
const DESC =
  "Sveriges community för husbil och husvagn. Ställ frågor, dela resor och ställplatser, felsök teknik och rösta upp de bästa svaren.";

export const Route = createFileRoute("/forum/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Fritidsforum – community för husbil & husvagn" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.akhusbilfritid.se/forum" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "AK Husbil & Fritid – Fritidsforum" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/forum" }],
  }),
  loader: async () => {
    const [cats, posts, board, unanswered] = await Promise.all([
      listForumCategories(),
      listForumPosts({ data: { sort: "hot", limit: 40 } }),
      forumLeaderboard({ data: { limit: 8 } }),
      listUnansweredQuestions({ data: { limit: 6 } }),
    ]);
    return { cats, posts, board, unanswered };
  },

  component: ForumIndex,
});

type Sort = "hot" | "new" | "top" | "unanswered";

function ForumIndex() {
  const initial = Route.useLoaderData();
  const cats = useServerFn(listForumCategories);
  const posts = useServerFn(listForumPosts);
  const board = useServerFn(forumLeaderboard);
  const unanswered = useServerFn(listUnansweredQuestions);
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const [sort, setSort] = useState<Sort>("hot");
  const [term, setTerm] = useState("");

  const { data: catData } = useQuery({
    queryKey: ["forum", "categories"],
    queryFn: () => cats(),
    initialData: initial.cats,
  });
  const { data: postData, isLoading } = useQuery({
    queryKey: ["forum", "posts", sort],
    queryFn: () => posts({ data: { sort, limit: 40 } }),
    initialData: sort === "hot" ? (initial.posts as any) : undefined,
  });
  const { data: boardData } = useQuery({
    queryKey: ["forum", "leaderboard"],
    queryFn: () => board({ data: { limit: 8 } }),
    initialData: initial.board,
  });
  const { data: unansweredData } = useQuery({
    queryKey: ["forum", "unanswered"],
    queryFn: () => unanswered({ data: { limit: 6 } }),
    initialData: initial.unanswered,
  });


  const rows = (postData?.posts ?? []) as ForumPostRow[];
  const members = boardData?.members ?? [];
  const openQuestions = unansweredData?.questions ?? [];


  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow">Community · Fritidsforum</p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] sm:text-5xl md:text-7xl">
              Prata <span className="font-italic-display text-accent">fritidsfordon</span>.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Ett svenskt community för husbil och husvagn. Ställ frågor, felsök teknik, dela
              ställplatser och resor — de bästa svaren röstas upp av andra ägare.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/forum/regler"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Forumregler
            </Link>
            <Link
              to={signedIn ? "/forum/nytt" : "/login"}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              <Plus size={16} /> Nytt inlägg
            </Link>
          </div>
        </div>

        {/* Kategorier */}
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {((catData?.categories ?? []) as any[]).map((c: any) => (
            <Link
              key={c.id}
              to="/forum/k/$slug"
              params={{ slug: c.slug }}
              className="group rounded-2xl border border-border bg-card p-4 transition hover:border-accent/50 hover:shadow-lg"
            >
              <p className="font-display text-lg transition group-hover:text-accent">{c.name}</p>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                {c.description}
              </p>
              <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Users size={11} /> {c.post_count} {c.post_count === 1 ? "tråd" : "trådar"}
              </p>
            </Link>
          ))}
        </div>

        {/* Sök */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (term.trim().length < 2) return;
            navigate({ to: "/forum/sok", search: { q: term.trim() } });
          }}
          className="mt-12 flex max-w-xl items-center gap-2"
        >
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Sök i forumet — fel, ställplatser, modeller…"
              className="w-full rounded-full border border-input bg-background py-3 pl-11 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <button className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">
            Sök
          </button>
        </form>

        {/* Sortering */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {(
            [
              { key: "hot", label: "Hetast", icon: Flame },
              { key: "new", label: "Senaste", icon: Clock },
              { key: "top", label: "Toppade", icon: TrendingUp },
              { key: "unanswered", label: "Obesvarade", icon: HelpCircle },
            ] as const
          ).map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition ${
                sort === s.key
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground/80 hover:bg-secondary"
              }`}
            >
              <s.icon size={14} /> {s.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-3">
            {isLoading && <p className="text-sm text-muted-foreground">Laddar trådar…</p>}
            {!isLoading && !rows.length && (
              <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                <p className="font-display text-xl">Inga trådar ännu</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Var först. Ställ en fråga eller dela din senaste resa.
                </p>
                <Link
                  to={signedIn ? "/forum/nytt" : "/login"}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                >
                  <Plus size={16} /> Skapa första inlägget
                </Link>
              </div>
            )}
            {rows.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            {openQuestions.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h2 className="inline-flex items-center gap-2 font-display text-lg">
                  <HelpCircle size={16} className="text-accent" /> Väntar på svar
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">Hjälp en ägare — inga svar ännu.</p>
                <ul className="mt-4 space-y-3">
                  {(openQuestions as any[]).map((q: any) => (
                    <li key={q.id}>
                      <Link
                        to="/forum/t/$slug"
                        params={{ slug: q.slug }}
                        className="line-clamp-2 text-sm font-medium leading-snug transition hover:text-accent"
                      >
                        {q.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {members.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h2 className="inline-flex items-center gap-2 font-display text-lg">
                  <Trophy size={16} className="text-accent" /> Topplista
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">Mest hjälpsamma medlemmarna just nu.</p>
                <ol className="mt-4 space-y-3">
                  {(members as any[]).map((m: any, i: number) => (
                    <li key={m.id} className="flex items-center gap-3">
                      <span className="w-5 shrink-0 text-sm font-bold text-muted-foreground">{i + 1}</span>
                      <Link
                        to="/forum/medlem/$id"
                        params={{ id: m.id }}
                        className="min-w-0 flex-1 truncate text-sm font-medium transition hover:text-accent"
                      >
                        {m.display_name || "Medlem"}
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {m.karma} karma{m.best > 0 ? ` · ${m.best}★` : ""}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <TagCloud />

            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-lg">Fukt- &amp; gasolrapporter</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Communityns egen erfarenhetsdatabas: riktiga fukttest och gasoltest per märke och modell.
              </p>
              <Link
                to="/erfarenheter"
                className="mt-3 inline-flex rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
              >
                Öppna databasen
              </Link>
            </div>


            <div className="rounded-2xl border border-border bg-secondary/40 p-5">
              <h2 className="font-display text-lg">Så funkar forumet</h2>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                <li>· Rösta upp svar som hjälper — de hamnar högst.</li>
                <li>· Ställer du en fråga kan du markera bästa svaret.</li>
                <li>· Dela gärna bilder från resan i Bild & resa.</li>
              </ul>
              <Link
                to={signedIn ? "/forum/nytt" : "/login"}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                <Plus size={15} /> Nytt inlägg
              </Link>
            </div>
          </aside>
        </div>

      </div>
    </section>
  );
}
